import { useEffect, useState } from "react";
import { Instagram, Globe, X, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Badge } from "../components/Pill";

const SPECIALTIES = ["All", "Wedding Planning", "Bridal Couture", "Photography", "Makeup and Beauty", "Choreography", "Music and DJs", "Content Creators", "Decor", "Floral", "Brand Partners"];

function EnquiryModal({ vendor, onClose }) {
  const [form, setForm] = useState({ couple_name: "", contact: "", wedding_date: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api(`/vendors/${vendor.id}/enquire`, { method: "POST", body: form });
      toast.success(`Enquiry sent. The editors will connect you with ${vendor.name}.`);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, testid, props = {}) => (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gold mb-2">{label}</label>
      <input
        data-testid={testid}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full border-2 border-ink/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-gold bg-white transition-colors duration-300"
        {...props}
      />
    </div>
  );

  return (
    <div data-testid="enquiry-modal" className="fixed inset-0 z-[70] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-5" onClick={onClose}>
      <div className="bg-cream rounded-[2rem] p-8 md:p-10 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <Badge tone="petal">Vendor Enquiry</Badge>
            <h3 className="mt-4 font-display uppercase text-2xl text-ink leading-tight">{vendor.name}</h3>
            <p className="mt-1 text-xs text-neutral-500">{vendor.specialty}{vendor.city ? ` · ${vendor.city}` : ""}</p>
          </div>
          <button onClick={onClose} data-testid="enquiry-close-button" aria-label="Close"
            className="w-10 h-10 rounded-full bg-blush flex items-center justify-center text-ink hover:bg-petal transition-colors duration-300">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={submit} className="mt-7 space-y-4">
          {field("couple_name", "Your Names", "enquiry-name-input", { required: true, placeholder: "Aisha and Rohan" })}
          {field("contact", "Email or Phone", "enquiry-contact-input", { required: true, placeholder: "you@example.com" })}
          {field("wedding_date", "Wedding Date (optional)", "enquiry-date-input", { placeholder: "December 2026, Udaipur" })}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gold mb-2">Message</label>
            <textarea
              data-testid="enquiry-message-input"
              rows={3}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell them a little about your celebration"
              className="w-full border-2 border-ink/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-gold bg-white transition-colors duration-300"
            />
          </div>
          <button
            type="submit"
            data-testid="enquiry-submit-button"
            disabled={busy}
            className="group w-full inline-flex items-center justify-center gap-3 bg-ink text-cream rounded-full px-7 py-3.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50"
          >
            {busy ? "Sending" : "Send Enquiry"} <Send size={14} />
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Vendors() {
  const [vendors, setVendors] = useState(null);
  const [filter, setFilter] = useState("All");
  const [enquireWith, setEnquireWith] = useState(null);

  useEffect(() => {
    setVendors(null);
    api(`/vendors?specialty=${encodeURIComponent(filter)}`)
      .then(setVendors)
      .catch(() => setVendors([]));
  }, [filter]);

  return (
    <main data-testid="vendors-page">
      <section className="bg-blush pt-36 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <Reveal><Badge tone="ink">The TBOI Directory</Badge></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display uppercase text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-ink leading-[0.98]">
              Artisans we trust
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-5 max-w-xl mx-auto text-neutral-600">
              Planners, couturiers, photographers and makers whose work fills our pages, and our respect.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="-mx-6 px-6 lg:mx-0 lg:px-0 overflow-x-auto no-scrollbar mb-8 lg:mb-12" data-testid="vendor-filters">
          <div className="flex lg:flex-wrap gap-2.5 lg:gap-3 w-max lg:w-auto">
            {SPECIALTIES.map((s) => (
              <button
                key={s}
                data-testid={`vendor-filter-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilter(s)}
                className={`whitespace-nowrap rounded-full px-4 lg:px-5 py-2 lg:py-2.5 text-[9px] lg:text-[10px] uppercase tracking-[0.18em] font-bold border-2 transition-colors duration-300 ${
                  filter === s ? "bg-ink text-cream border-ink" : "border-ink/15 text-ink/60 hover:border-gold hover:text-gold"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {!vendors ? (
          <p className="font-elegant italic text-2xl text-gold animate-pulse">Opening the directory</p>
        ) : vendors.length === 0 ? (
          <p className="text-neutral-500" data-testid="vendors-empty">No artisans listed under this specialty yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {vendors.map((v, i) => (
              <Reveal key={v.id} delay={0.05 * (i % 3)}>
                <article data-testid={`vendor-card-${v.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="group">
                  {v.image ? (
                    <div className="overflow-hidden rounded-[1.75rem]">
                      <img
                        src={v.image}
                        alt={v.name}
                        loading="lazy"
                        className="w-full aspect-[4/5] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[4/5] rounded-[1.75rem] bg-blush border-2 border-gold/10 flex flex-col items-center justify-center gap-4 transition-colors duration-300 group-hover:border-gold/40">
                      <span className="w-20 h-20 rounded-full bg-white border-2 border-champagne/50 flex items-center justify-center">
                        <span className="font-elegant italic text-4xl text-gold">{v.name.charAt(0)}</span>
                      </span>
                      <span className="font-display uppercase text-lg text-ink/70 text-center px-6 leading-tight">{v.name}</span>
                    </div>
                  )}
                  <div className="mt-5 flex flex-wrap gap-2">
                    <Badge tone="blush">{v.specialty}</Badge>
                    {v.city && <Badge tone="white">{v.city}</Badge>}
                  </div>
                  <h2 className="mt-3 font-display uppercase text-2xl md:text-[1.7rem] text-ink leading-tight">{v.name}</h2>
                  {v.description && <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{v.description}</p>}
                  <div className="mt-5 flex items-center gap-3 flex-wrap">
                    <button
                      onClick={() => setEnquireWith(v)}
                      data-testid={`vendor-enquire-${i}`}
                      className="group/btn inline-flex items-center gap-2 bg-ink text-cream rounded-full px-6 py-3 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-gold hover:text-ink transition-colors duration-300"
                    >
                      Enquire <Send size={13} />
                    </button>
                    {v.instagram_url && (
                      <a href={v.instagram_url} target="_blank" rel="noreferrer" data-testid={`vendor-ig-${i}`} aria-label="Instagram"
                        className="w-11 h-11 rounded-full border-2 border-ink/15 flex items-center justify-center text-ink/60 hover:border-rose hover:text-rose transition-colors duration-300">
                        <Instagram size={16} />
                      </a>
                    )}
                    {v.website_url && (
                      <a href={v.website_url} target="_blank" rel="noreferrer" data-testid={`vendor-web-${i}`} aria-label="Website"
                        className="w-11 h-11 rounded-full border-2 border-ink/15 flex items-center justify-center text-ink/60 hover:border-gold hover:text-gold transition-colors duration-300">
                        <Globe size={16} />
                      </a>
                    )}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}

        <Reveal className="mt-24">
          <div className="bg-ink text-cream rounded-[2.5rem] px-8 py-14 md:py-16 text-center">
            <Badge tone="gold">For the Wedding Industry</Badge>
            <h2 className="mt-6 font-display uppercase text-3xl md:text-5xl max-w-2xl mx-auto leading-[1.02]">
              Are you a wedding artisan with work worth chronicling?
            </h2>
            <p className="mt-5 text-cream/60 max-w-xl mx-auto text-sm leading-relaxed">
              The directory is curated by our editors. Share your portfolio with us on Instagram and tell us why your
              craft belongs in these pages.
            </p>
            <a
              href="https://www.instagram.com/thebridesofindia"
              target="_blank"
              rel="noreferrer"
              data-testid="vendor-cta-instagram"
              className="mt-9 inline-flex items-center gap-3 bg-champagne text-ink rounded-full px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-cream transition-colors duration-300"
            >
              <Instagram size={15} /> Write to the Editors
            </a>
          </div>
        </Reveal>
      </section>

      {enquireWith && <EnquiryModal vendor={enquireWith} onClose={() => setEnquireWith(null)} />}
    </main>
  );
}
